import * as d3 from 'd3';
import rotationScale from './RotationScale';
import { GaugeDataFormat } from './GaugeDataFormat';
import { GaugeOptions } from './GaugeOptions';
import { GaugeFormat } from './GaugeFormat';
import { LabelFormat } from './LabelFormat';
import { TickFormat } from './TickFormat';
import { TargetFormat } from './TargetFormat';

function ticks(scale: d3.scale.Linear<number, number>, ticksCount: number): number[] {
    const domain = scale.domain();
    return d3.range(domain[0], domain[1] * 1.01, (domain[1] - domain[0]) / (ticksCount - 1));
}

function getTargetItem(
    showTarget: boolean,
    d: GaugeDataFormat,
    scale: d3.scale.Linear<number, number>,
    outerRadius: number,
    innerRadius: number,
    fmt: (n: number) => string,
    labelValues: LabelFormat[],
    labelFontSize: number,
    w: number): TargetFormat {
    const targetArrowSize = 6;
    let targetItem: TargetFormat;
    if (showTarget) {
        if (d.serie) {
            let { targetValue } = d.serie;
            let formattedValue = fmt(targetValue);
            if (targetValue === null || targetValue === undefined) {
                targetValue = 0; // Set to 0 to avoid NaN everywhere
                formattedValue = null;
            }
            const angle = scale(targetValue);
            const cos = Math.cos(angle - Math.PI / 2);
            targetItem = {
                size: outerRadius - innerRadius - 1,
                line: {
                    x1: -innerRadius,
                    x2: -outerRadius + 1,
                    y1: 0,
                    y2: 0,
                    rotate: rotationScale(scale(targetValue))
                },
                value: d.serie.targetValue,
                formattedValue: formattedValue,
                arrowPosition: {
                    x: (outerRadius + targetArrowSize) * cos,
                    y: (outerRadius + targetArrowSize) * Math.sin(angle - Math.PI / 2),
                    rotate: rotationScale(scale(targetValue)) - 90
                }
            };
            if (formattedValue !== null && formattedValue !== undefined) {
                labelValues.push(getLabelFormat(targetValue, angle, w, cos, fmt, labelFontSize));
            }
        }
    }
    return targetItem;
}


export function mapToGaugeFormat(
    data: GaugeDataFormat[],
    options: GaugeOptions,
    outerWidth: number,
    scalingRatio: number = 1
): GaugeFormat[] {
    const { labelsCount, range, ticksCount, thickness } = options;
    let { tickSize } = options;

    if (ticksCount <= 0) {
        tickSize = 0;
    }
    tickSize *= scalingRatio || 1;

    const constant = 10;
    let ratio = 0;
    if (labelsCount > 0) {
        ratio = 1 / (1 + ((2 * (1 - thickness) / constant)));
    }
    const outerRadius = ratio * (outerWidth - 2 * tickSize);
    const innerRadius = outerRadius * (1 - thickness);
    const labelSize = labelsCount <= 0 ? 0 : innerRadius / constant;
    const arc = d3.svg.arc()
        .outerRadius(outerRadius)
        .innerRadius(innerRadius);
    const scale = d3.scale.linear()
        .range(range);
    const w = outerRadius + tickSize + labelSize;
    const labelFontSize = innerRadius / 10;
    const toBind: GaugeFormat[] = data.map(d => {
        const min = d3.min(d.bands, d => d.min);
        const max = d3.max(d.bands, d => d.max);
        scale.domain([min, max]);
        const fmt = d3.format(d.format);

        const tickValues: TickFormat[] = ticksCount > 0
            ? ticks(scale, ticksCount)
                .map(t => {
                    return {
                        value: t,
                        formattedValue: fmt(t),
                        line: {
                            x1: -outerRadius - tickSize,
                            x2: -outerRadius,
                            y1: 0,
                            y2: 0,
                            rotate: rotationScale(scale(t))
                        }
                    };
                })
            : [];

        const labelValues: LabelFormat[] = getLabelValues(labelsCount, scale, w, fmt, labelFontSize);

        const targetItem: TargetFormat = getTargetItem(options.showTarget, d, scale, outerRadius, innerRadius, fmt, labelValues,
            labelFontSize, w);
        const overMin = min - ((max - min) * 0.05);
        const overMax = max + ((max - min) * 0.05);
        let limitedValue = d.value;
        if (d.value < overMin) {
            limitedValue = overMin;
        }
        if (d.value > overMax) {
            limitedValue = overMax;
        }
        return {
            value: d.value,
            formattedValue: fmt(d.value),
            needle: {
                size: (outerRadius - innerRadius) * 2,
                rotation: rotationScale(scale(limitedValue)),
                position: outerRadius * .95
            },
            fontSize: innerRadius / 3,
            bands: d.bands.map(b => {
                return {
                    arc: arc
                        .startAngle(scale(b.min))
                        .endAngle(scale(b.max))(b as any),
                    isCurrent: b.min <= d.value && b.max >= d.value,
                    color: b.color,
                    classed: b.classed
                };
            }),
            ticks: tickValues,
            labels: labelValues,
            target: targetItem
        };
    });
    return toBind;
}


function getLabelFormat(targetValue: number, angle: number, w: number, cos: number, fmt: (n: number) => string, labelFontSize: number): LabelFormat {
    return {
        value: targetValue,
        angle,
        position: {
            x: w * cos,
            y: w * Math.sin(angle - Math.PI / 2)
        },
        formatted: fmt(targetValue),
        anchor: getAnchor(cos),
        fontSize: labelFontSize
    };
}

function getLabelValues(labelsCount: number, scale: d3.scale.Linear<number, number>, w: number, fmt: (n: number) => string, labelFontSize: number): LabelFormat[] {
    return labelsCount >= 0
        ? ticks(scale, labelsCount)
            .map(s => {
                const angle = scale(s);
                const cos = Math.cos(angle - Math.PI / 2);
                return getLabelFormat(s, angle, w, cos, fmt, labelFontSize);
            })
        : [];
}


function getAnchor(cos: number) {
    let anchor: string;
    if (cos > 1e-7) {
        anchor = 'start';
    } else if (cos < -1e-7) {
        anchor = 'end';
    } else {
        anchor = 'middle';
    }
    return anchor;
}